import { useNavigate, useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import {
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import Loader from "../../components/Loader";
import useGetAppAdvancedSurveysSummary from "../../hooks/useGetAppAdvancedSurveysSummary";
import { formatDate } from "../../utils/helpers";

const PatientAdvancedSurveys = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  setActive("advanced-surveys");
  setBreadcrumbTitle("ePRO");

  const { data: summaryData, isLoading } = useGetAppAdvancedSurveysSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const allowedRoles = [
    "CRO Admin",
    "Investigator",
    "Sub_investigator",
    "Cro_coordinator",
    "Data Manager",
    "Inv_coordinator",
    "Super Admin",
    "Reviewer",
  ];

  const sessions = summaryData?.sessions || [];
  const hasAnySessions = sessions.length > 0;

  const routeTo = (id, visitName) => {
    navigate(`/patient/${_id_}/advanced-surveys/${id}?trial=${trialFilter}`, {
      state: { visitName },
    });
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack spacing={2} mb={2}>
        <Typography variant="h6">ePRO</Typography>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {allowedRoles.includes(role) && <TableCell sx={headerCellSx}>Visit Name</TableCell>}

              <TableCell sx={headerCellSx}>Visit Type</TableCell>

              {allowedRoles.includes(role) && (
                <>
                  <TableCell sx={headerCellSx}>Visit Open Date</TableCell>
                  <TableCell sx={headerCellSx}>Visit Close Date</TableCell>
                  <TableCell sx={headerCellSx}>App Start Date</TableCell>
                  <TableCell sx={headerCellSx}>App Completed Date</TableCell>
                  <TableCell sx={headerCellSx}>App Status</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Loading */}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {/* Data */}
            {!isLoading &&
              hasAnySessions &&
              sessions.map((session) => (
                <TableRow
                  key={session.session_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => routeTo(session.session_id, session.visit_name)}
                >
                  {allowedRoles.includes(role) && (
                    <TableCell sx={bodyCellSx}>{session.visit_name}</TableCell>
                  )}

                  <TableCell sx={bodyCellSx}>{session.visit_type || "Regular"}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_open_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_close_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.inapp_start_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.inapp_completed_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{session.inapp_visit_status || "-"}</TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        routeTo(session.session_id, session.visit_name);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {/* Empty State */}
            {!isLoading && !hasAnySessions && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No visit sessions with app activity found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PatientAdvancedSurveys;
